import { ConfirmModal, Menu, MenuItem } from 'components';
import React, { useState } from 'react';
import Alert from 'react-s-alert';
import { observer } from 'mobx-react';
import { RiMore2Fill } from 'react-icons/ri';
import i18next from 'i18next';
import { frameStore } from 'apps/components/Frame/appManager';
import { analytics } from 'core/analytics';
import { SyncRepoAppManager } from '../appManager';

enum ActionItem {
  Reset = 'Reset',
  Disconnect = 'Disconnect',
}

type SyncedDropdownProps = {
  loading?: boolean;
  syncRepoAppManager: SyncRepoAppManager;
};

export const SyncedDropdown: React.FC<SyncedDropdownProps> = observer(
  ({ syncRepoAppManager, loading }) => {
    const [avatarEl, setAvatarEl] = useState<null | SVGElement>(null);
    const [shouldLoading, setShouldLoading] = useState<boolean>(false);
    const [actionType, setActionType] = useState<ActionItem | null>();
    const initAnchorElement = () => setAvatarEl(null);
    const handleAvatarClick = (event: React.MouseEvent<SVGElement>) => {
      setAvatarEl(avatarEl ? null : event.currentTarget);
    };

    const resetActionType = () => {
      setActionType(null);
    };

    const items = [
      {
        type: ActionItem.Reset,
        onClick: () => {
          setActionType(ActionItem.Reset);
        },
      },
      {
        type: ActionItem.Disconnect,
        onClick: async () => {
          setActionType(ActionItem.Disconnect);
        },
      },
    ];

    const actionMap = {
      [ActionItem.Reset]: {
        confirm: async () => {
          setShouldLoading(true);

          const [success, err] = await syncRepoAppManager.resetProject(
            frameStore.project.id,
          );

          if (err) {
            analytics.logError(err);
            Alert.error(
              i18next.t(
                'There has been an error resetting to the project. Please try again later.',
              ),
            );
          }

          if (success) {
            await syncRepoAppManager.PullAndSync();
            resetActionType();
          }

          setShouldLoading(false);
        },
        confirmMessage: i18next.t(
          'Reset your local folder to the latest cloud contribution. Local conflicts will be lost.',
        ),
      },
      [ActionItem.Disconnect]: {
        confirm: async () => {
          setShouldLoading(true);
          syncRepoAppManager
            .Disconnect()
            .then(() => {
              resetActionType();
            })
            .catch(err => {
              if (err) {
                analytics.logError(err);
                Alert.error(
                  i18next.t(
                    'There has been an error disconnecting from the project. Please try again later.',
                  ),
                );
              }
            })
            .finally(() => {
              setShouldLoading(false);
            });
        },
        confirmMessage: i18next.t(
          'Disconnect your local folder from the project. The app will no longer track your local changes.',
        ),
      },
    };

    const topGap = 8;
    const menuWidth = 148;
    const confirmModalWidth = 632;
    const shouldOpenConfirm = !!actionType;

    return (
      <div>
        <RiMore2Fill
          className=' cursor-pointer text-black hover:text-blue-500'
          onClick={e => {
            handleAvatarClick(e);
          }}
        />
        <Menu
          style={{ width: menuWidth, minWidth: 'unset' }}
          open={Boolean(avatarEl)}
          anchorEl={avatarEl as any}
          onClose={initAnchorElement}
          onClick={handleAvatarClick as any}
          placement='customize-position'
          customizePosition={({ top, height, width, left, modalStyle }) => {
            return {
              left: `calc(${left - menuWidth + width}px - ${
                modalStyle.borderWidth
              })`,
              top: `calc(${top + height + topGap}px - ${
                modalStyle.borderWidth
              } + ${window.scrollY}px)`,
            };
          }}
        >
          {items.map(({ onClick, type }) => {
            return (
              <MenuItem
                onClick={() => {
                  onClick();
                }}
                key={type}
              >
                {type}
              </MenuItem>
            );
          })}
        </Menu>
        <ConfirmModal
          titleStyle={{
            width: confirmModalWidth,
            fontSize: 16,
          }}
          contentProps={{
            sx: {
              maxWidth: 'unset',
              minWidth: confirmModalWidth,
            },
          }}
          paperStyle={{ maxWidth: 'unset' }}
          titleClassName='font-normal text-center text-base justify-center'
          open={shouldOpenConfirm}
          title={actionType && actionMap[actionType].confirmMessage}
          loading={shouldLoading || loading}
          onConfirm={() => {
            actionMap[actionType].confirm();
          }}
          onClose={() => {
            resetActionType();
          }}
        />
      </div>
    );
  },
);
